import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    createFilterOptions,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { CONTENITORE_TABELLA, TESTATA_TABELLA } from '../common/stiliTabella';

export const NOTA_DUE_PORTE =
    'Un gruppo dà accesso ai suoi dati per due vie: i tipi di Codex che contiene e le code ' +
    'di lavoro che apre. Le due vie si sommano, e stanno qui accanto per questo: chi ' +
    'instrada un processo verso una coda concede la vista su quei documenti a tutto il ' +
    'gruppo, e vedendo solo una delle due metà non se ne accorgerebbe.';

const VUOTO_CODE = 'Nessuna coda: da questo gruppo non passa nessuna attività.';
const VUOTO_TIPI = 'Nessun tipo di Codex assegnato a questo gruppo.';

const filtraTipi = createFilterOptions({
    stringify: (t) => [t.code, t.description, t.id].filter(Boolean).join(' '),
});
const filtraCode = createFilterOptions({
    stringify: (c) => [c.code, c.candidateGroup].filter(Boolean).join(' '),
});

/**
 * Il contenuto di un gruppo: i tipi di Codex **e** le code, sulla stessa schermata.
 *
 * L'accostamento è un requisito del disegno, non una scelta di layout: sono due porte sullo
 * stesso dato e mostrarne una sola inganna chi configura. Due tab, o due pagine, avrebbero
 * reso invisibile la metà non aperta proprio a chi sta decidendo cosa un reparto vede.
 *
 * `readOnly` nasconde i selettori e le rimozioni invece di disabilitarli: mostrare
 * un'azione che il backend rifiuterebbe è il difetto che questa pagina evita.
 */
export default function GroupContentPanel({
    candidateGroups,
    codexTypeIds,
    codeDisponibili,
    tipiDisponibili,
    readOnly = false,
    onAggiungiCoda,
    onRimuoviCoda,
    onAggiungiTipo,
    onRimuoviTipo,
}) {
    const [nuoveCode, setNuoveCode] = useState([]);
    const [nuoviTipi, setNuoviTipi] = useState([]);

    // Una coda o un tipo assegnato che non compare più nel catalogo — cancellato altrove —
    // resta visibile col solo identificatore: sparire in silenzio sarebbe il difetto
    // peggiore, perché è proprio la riga che qualcuno deve togliere.
    const etichettaCoda = useMemo(() => {
        const mappa = new Map();
        (codeDisponibili || []).forEach((coda) => {
            if (coda.candidateGroup) {
                mappa.set(coda.candidateGroup, coda);
            }
        });
        return (candidateGroup) => {
            const coda = mappa.get(candidateGroup);
            return coda && coda.code && coda.code !== candidateGroup
                ? `${coda.code} (${candidateGroup})`
                : candidateGroup;
        };
    }, [codeDisponibili]);

    const etichettaTipo = useMemo(() => {
        const mappa = new Map();
        (tipiDisponibili || []).forEach((tipo) => mappa.set(tipo.id, tipo));
        return (id) => {
            const tipo = mappa.get(id);
            return tipo && tipo.code ? tipo.code : id;
        };
    }, [tipiDisponibili]);

    // Le voci gia' nel gruppo restano nell'elenco, spuntate e spente: la stessa scelta del
    // selettore dei gruppi in «Concedi un ruolo». Toglierle farebbe sembrare mancante una
    // coda che c'e', proprio a chi la sta cercando.
    const codeAssegnate = useMemo(() => new Set(candidateGroups || []), [candidateGroups]);
    const tipiAssegnati = useMemo(() => new Set(codexTypeIds || []), [codexTypeIds]);

    const codeSelezionabili = useMemo(
        () => (codeDisponibili || []).filter((coda) => coda.candidateGroup),
        [codeDisponibili],
    );
    const tipiSelezionabili = useMemo(
        () => (tipiDisponibili || []).filter((tipo) => tipo.id),
        [tipiDisponibili],
    );

    const aggiungiCode = () => {
        const scelte = nuoveCode.map((coda) => coda.candidateGroup)
            .filter((coda) => !codeAssegnate.has(coda));
        setNuoveCode([]);
        if (scelte.length > 0) {
            onAggiungiCoda(scelte);
        }
    };

    const aggiungiTipi = () => {
        const scelti = nuoviTipi.map((tipo) => tipo.id).filter((id) => !tipiAssegnati.has(id));
        setNuoviTipi([]);
        if (scelti.length > 0) {
            onAggiungiTipo(scelti);
        }
    };

    // Un selettore multiplo con le caselle, come quello dei gruppi nella vista utente.
    const selettore = ({
        label, opzioni, valore, onCambia, filtra, chiave, etichetta, dettaglio, assegnate,
        onAggiungi, plurale,
    }) => {
        const giaNelGruppo = (o) => assegnate.has(chiave(o));
        return (
            <Box sx={{ mb: 2 }}>
                <Autocomplete
                    multiple
                    size="small"
                    disableCloseOnSelect
                    limitTags={3}
                    options={opzioni}
                    value={valore}
                    onChange={(_, scelte) => onCambia(scelte)}
                    filterOptions={filtra}
                    getOptionLabel={etichetta}
                    isOptionEqualToValue={(a, b) => chiave(a) === chiave(b)}
                    getOptionDisabled={giaNelGruppo}
                    noOptionsText="Nessuna voce trovata"
                    renderOption={({ key, ...props }, o, { selected }) => (
                        <li key={key} {...props}>
                            <Checkbox
                                size="small"
                                checked={selected || giaNelGruppo(o)}
                                sx={{ mr: 1, p: 0.5 }}
                            />
                            <Box>
                                <Typography variant="body2">{etichetta(o)}</Typography>
                                {(giaNelGruppo(o) || dettaglio(o)) && (
                                    <Typography variant="caption" color="text.secondary">
                                        {giaNelGruppo(o) ? 'già nel gruppo' : dettaglio(o)}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={label}
                            placeholder={valore.length === 0 ? 'Cerca' : ''}
                        />
                    )}
                />
                {/* Il pulsante sotto e non accanto, come in «Concedi un ruolo»: la colonna e'
                    stretta, e accanto il conteggio («Aggiungi 2 code») usciva dal bordo. */}
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={onAggiungi}
                        disabled={valore.length === 0}
                    >
                        {valore.length > 1 ? `Aggiungi ${valore.length} ${plurale}` : 'Aggiungi'}
                    </Button>
                </Stack>
            </Box>
        );
    };
    const colonna = (titolo, righe, vuoto, etichetta, onRimuovi, ariaPrefisso) => (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {titolo}
            </Typography>
            {righe.length === 0 ? (
                <Alert severity="info">{vuoto}</Alert>
            ) : (
                <TableContainer {...CONTENITORE_TABELLA}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={TESTATA_TABELLA}>
                                <TableCell>{titolo}</TableCell>
                                {!readOnly && <TableCell align="right">Rimuovi</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {righe.map((riga) => (
                                <TableRow key={riga}>
                                    <TableCell>{etichetta(riga)}</TableCell>
                                    {!readOnly && (
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                aria-label={`${ariaPrefisso} ${riga}`}
                                                onClick={() => onRimuovi(riga)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {NOTA_DUE_PORTE}
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    {!readOnly && selettore({
                        label: 'Tipi di Codex da aggiungere',
                        opzioni: tipiSelezionabili,
                        valore: nuoviTipi,
                        onCambia: setNuoviTipi,
                        filtra: filtraTipi,
                        chiave: (tipo) => tipo.id,
                        etichetta: (tipo) => tipo.code || tipo.id,
                        dettaglio: (tipo) => tipo.description,
                        assegnate: tipiAssegnati,
                        onAggiungi: aggiungiTipi,
                        plurale: 'tipi',
                    })}
                    {colonna('Tipi di Codex', codexTypeIds || [], VUOTO_TIPI, etichettaTipo,
                        onRimuoviTipo, 'rimuovi tipo')}
                </Grid>

                <Grid item xs={12} md={6}>
                    {!readOnly && selettore({
                        label: 'Code da aggiungere',
                        opzioni: codeSelezionabili,
                        valore: nuoveCode,
                        onCambia: setNuoveCode,
                        filtra: filtraCode,
                        chiave: (coda) => coda.candidateGroup,
                        etichetta: (coda) => coda.code || coda.candidateGroup,
                        dettaglio: (coda) => (coda.code && coda.code !== coda.candidateGroup
                            ? coda.candidateGroup : null),
                        assegnate: codeAssegnate,
                        onAggiungi: aggiungiCode,
                        plurale: 'code',
                    })}
                    {colonna('Code di lavoro', candidateGroups || [], VUOTO_CODE, etichettaCoda,
                        onRimuoviCoda, 'rimuovi coda')}
                </Grid>
            </Grid>
        </Box>
    );
}

GroupContentPanel.propTypes = {
    candidateGroups: PropTypes.arrayOf(PropTypes.string),
    codexTypeIds: PropTypes.arrayOf(PropTypes.string),
    /** Il catalogo delle code (`GET /v1/queues`), per mostrarne il codice leggibile. */
    codeDisponibili: PropTypes.array,
    /** Il catalogo dei tipi di Codex (`GET /v1/codex-types`). */
    tipiDisponibili: PropTypes.array,
    readOnly: PropTypes.bool,
    /** Riceve l'elenco dei candidate group scelti, uno o piu'. */
    onAggiungiCoda: PropTypes.func,
    onRimuoviCoda: PropTypes.func,
    /** Riceve l'elenco degli id dei tipi scelti, uno o piu'. */
    onAggiungiTipo: PropTypes.func,
    onRimuoviTipo: PropTypes.func,
};
